/**
 * PatrolLocationFilters
 *
 * Filter controls for patrol location circles on the map:
 * - Scope dropdown: Active Patrols | Today's Patrols | All Sites (30 days)
 * - Date picker: Select date to view patrols (Active Patrols disabled for past dates)
 *
 * Note: "Active Patrols" shows real-time guard positions (today only).
 * Other scopes use the API to fetch patrol data.
 */

import React, { useCallback, useMemo } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  TextField,
  SelectChangeEvent,
} from '@mui/material';
import {
  DatePicker,
  LocalizationProvider,
  AdapterDateFns,
} from 'components/mui';
import { PatrolLocationScope } from '../../../../../api/dispatch';
import styles from './PatrolLocationFilters.module.css';

export interface PatrolLocationFiltersProps {
  /** Current scope selection */
  scope: PatrolLocationScope;
  /** Current date selection */
  date: Date;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Callback when scope changes */
  onScopeChange: (scope: PatrolLocationScope) => void;
  /** Callback when date changes */
  onDateChange: (date: Date) => void;
}

/** Scope options with labels and descriptions */
const SCOPE_OPTIONS: Array<{
  value: PatrolLocationScope;
  label: string;
  description: string;
}> = [
  {
    value: 'active',
    label: 'Active Patrols',
    description: 'Currently in-progress patrols with real-time guard positions (today only)',
  },
  {
    value: 'today',
    label: "Today's Patrols",
    description: 'All patrols scheduled for the selected date',
  },
  {
    value: 'all_sites',
    label: 'All Sites (30 days)',
    description: 'All sites with patrols in the last 30 days',
  },
];

/**
 * Filter controls for patrol location circles
 */
const PatrolLocationFilters: React.FC<PatrolLocationFiltersProps> = ({
  scope,
  date,
  disabled = false,
  onScopeChange,
  onDateChange,
}) => {
  // Ensure we have a valid date object
  const safeDate = useMemo(() => {
    try {
      if (!date) return new Date();
      const d = date instanceof Date ? date : new Date(date);
      return isNaN(d.getTime()) ? new Date() : d;
    } catch {
      return new Date();
    }
  }, [date]);

  // Check if selected date is today
  const isToday = useMemo(() => {
    const today = new Date();
    return (
      safeDate.getDate() === today.getDate() &&
      safeDate.getMonth() === today.getMonth() &&
      safeDate.getFullYear() === today.getFullYear()
    );
  }, [safeDate]);

  // Check if selected date is in the past
  const isPastDate = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(safeDate);
    selectedDate.setHours(0, 0, 0, 0);
    return selectedDate < today;
  }, [safeDate]);

  // Handle scope change
  const handleScopeChange = useCallback(
    (event: SelectChangeEvent<PatrolLocationScope>) => {
      onScopeChange(event.target.value as PatrolLocationScope);
    },
    [onScopeChange]
  );

  // Handle date change
  const handleDateChange = useCallback(
    (newDate: Date | null) => {
      if (newDate) {
        onDateChange(newDate);
        // If switching to past date and scope is 'active', auto-switch to 'today'
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selected = new Date(newDate);
        selected.setHours(0, 0, 0, 0);
        if (selected < today && scope === 'active') {
          onScopeChange('today');
        }
      }
    },
    [onDateChange, onScopeChange, scope]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box className={styles.container}>
        {/* Scope Dropdown */}
        <FormControl size="small" className={styles.scopeSelect} disabled={disabled}>
          <InputLabel id="patrol-scope-label">Filter</InputLabel>
          <Select
            labelId="patrol-scope-label"
            value={scope}
            label="Filter"
            onChange={handleScopeChange}
          >
            {SCOPE_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={option.value === 'active' && isPastDate}
              >
                <Tooltip title={option.description} placement="right" arrow>
                  <span style={{ width: '100%' }}>{option.label}</span>
                </Tooltip>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Date Picker - hidden for 'all_sites' scope */}
        {scope !== 'all_sites' && (
          <DatePicker
            label="Date"
            value={safeDate}
            onChange={handleDateChange}
            disabled={disabled}
            views={['year', 'month', 'day']}
            openTo="day"
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                className={styles.datePicker}
              />
            )}
          />
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default PatrolLocationFilters;
